import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error' | 'submitting'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), href: '/' },
    { label: t('breadcrumb.about') },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const submitAddr = 'https://readdy.ai/api/form/d9pmgqhn2trhb35gvhtg';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError(serverMsg || t('about.formError'));
          setFormStatus('error');
        } else {
          setFormError(serverMsg || t('about.formError'));
          setFormStatus('error');
        }
      }
    } catch {
      setFormError(t('about.formError'));
      setFormStatus('error');
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91视频网站',
    url: 'https://www.91video.com',
    description: '91视频网站是一个提供海量高清视频内容的在线观看平台。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9191',
      contactType: 'customer service',
      email: 'contact@91video.com',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '望京科技园A座',
      postalCode: '100102',
      addressCountry: 'CN',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: t('about.title'),
    url: 'https://www.91video.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '91视频网站',
      url: 'https://www.91video.com',
    },
  };

  const testimonials = [
    { text: t('about.testimonial1'), name: t('about.user1'), role: t('about.user1Role') },
    { text: t('about.testimonial2'), name: t('about.user2'), role: t('about.user2Role') },
    { text: t('about.testimonial3'), name: t('about.user3'), role: t('about.user3Role') },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center">
              {t('about.brandTitle')}
            </h1>
            <p className="mt-6 text-foreground-700 leading-relaxed text-center max-w-[700px] mx-auto">
              {t('about.brandDesc')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                  <i className="ri-focus-3-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900">{t('about.brandMission')}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{t('about.brandMissionDesc')}</p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 mb-4">
                  <i className="ri-eye-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900">{t('about.brandVision')}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{t('about.brandVisionDesc')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center">
              {t('about.reputationTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
              {testimonials.map((item, idx) => (
                <div key={idx} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{item.text}</p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 font-semibold text-sm">
                      {item.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center">
              {t('about.contactTitle')}
            </h2>
            <p className="mt-2 text-sm text-foreground-500 text-center">
              {t('about.contactSubtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">{t('about.email')}</p>
                    <p className="text-sm font-medium text-foreground-800">{t('about.emailValue')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">{t('about.phone')}</p>
                    <p className="text-sm font-medium text-foreground-800">{t('about.phoneValue')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">{t('about.address')}</p>
                    <p className="text-sm font-medium text-foreground-800">{t('about.addressValue')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">{t('about.workHours')}</p>
                    <p className="text-sm font-medium text-foreground-800">{t('about.workHoursValue')}</p>
                  </div>
                </div>
              </div>

              <div>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div>
                    <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700">{t('about.formName')}</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700">{t('about.formEmail')}</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700">{t('about.formMessage')}</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : t('about.formSubmit')}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-secondary-600 text-center">{t('about.formSuccess')}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-primary-600 text-center">{formError || t('about.formError')}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}